import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';

import Icon from '../../components/AppIcon';

import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { vehicleService } from '../../services/vehicleService';


import VehicleCard from './components/VehicleCard';
import DriverSelectionModal from './components/DriverSelectionModal';
import BookingModal from './components/BookingModal';
import PricingCalculator from './components/PricingCalculator';

const VehicleRental = () => {
  const { variants, transition } = usePageTransition();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [selectedType, setSelectedType] = useState('all');
  const [selectedTransmission, setSelectedTransmission] = useState('all');
  const [priceRange, setPriceRange] = useState('all');

  // Modals
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    loadVehicles();

    // Subscribe to real-time changes
    const unsubscribe = vehicleService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setVehicles(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setVehicles(prev => prev?.map(v => v?.id === payload?.new?.id ? payload?.new : v));
      } else if (payload?.eventType === 'DELETE') {
        setVehicles(prev => prev?.filter(v => v?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [vehicles, selectedType, selectedTransmission, priceRange]);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const data = await vehicleService?.getAvailable();
      setVehicles(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...vehicles];

    if (selectedType !== 'all') {
      filtered = filtered?.filter(v => v?.vehicleType === selectedType);
    }

    if (selectedTransmission !== 'all') {
      filtered = filtered?.filter(v => v?.transmission === selectedTransmission);
    }

    if (priceRange !== 'all') {
      const [min, max] = priceRange?.split('-')?.map(Number);
      filtered = filtered?.filter(v => {
        const rate = v?.dailyRate;
        if (max) {
          return rate >= min && rate <= max;
        }
        return rate >= min;
      });
    }

    setFilteredVehicles(filtered);
  };

  const handleRentVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDriverModal(true);
  };

  const handleDriverSelected = (driver) => {
    setSelectedDriver(driver);
    setShowDriverModal(false);
    setShowBookingModal(true);
  };

  const handleSkipDriver = () => {
    setSelectedDriver(null);
    setShowDriverModal(false);
    setShowBookingModal(true);
  };

  const handleBookingComplete = () => {
    setShowBookingModal(false);
    setSelectedVehicle(null);
    setSelectedDriver(null);
    loadVehicles();
  };

  const vehicleTypeOptions = [
    { value: 'all', label: 'Tous les types' },
    { value: 'Sedan', label: 'Berline' },
    { value: 'SUV', label: 'SUV' },
    { value: 'Van', label: 'Van' },
    { value: 'Truck', label: 'Camion' },
    { value: 'Luxury', label: 'Luxe' },
    { value: 'Bus', label: 'Bus' }
  ];

  const transmissionOptions = [
    { value: 'all', label: 'Toutes transmissions' },
    { value: 'Automatic', label: 'Automatique' },
    { value: 'Manual', label: 'Manuelle' }
  ];

  const priceRangeOptions = [
    { value: 'all', label: 'Tous les prix' },
    { value: '0-100000', label: '0 - 100,000 CDF' },
    { value: '100000-150000', label: '100,000 - 150,000 CDF' },
    { value: '150000-200000', label: '150,000 - 200,000 CDF' },
    { value: '200000-999999', label: '200,000+ CDF' }
  ];

  return (
    <>
      <Helmet>
        <title>Location de Véhicules Kinshasa - ORAZEK Investment</title>
        <meta name="description" content="Louez des véhicules premium à Kinshasa. Berlines, SUVs, Bus et 4x4. Avec ou sans chauffeur. Prix compétitifs et assurance incluse." />
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <Header />

        <motion.main
          className="flex-1"
          initial="initial"
          animate="animate"
          exit="exit"
          variants={variants}
          transition={transition}
        >
          {/* Hero Section */}
          <section className="relative py-20 md:py-28 lg:py-32 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
            {/* Animated blob backgrounds */}
            <motion.div
              className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-blue-500/15 blur-[150px]"
              animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
              transition={{ duration: 10, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[120px]"
              animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 8, repeat: Infinity, delay: 2 }}
            />

            {/* Noise texture overlay */}
            <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
              <motion.div
                className="max-w-4xl mx-auto text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-5 py-2.5 mb-6">
                  <Icon name="Car" size={18} className="text-blue-400" />
                  <span className="text-sm font-medium text-blue-200">Location de Véhicules Premium</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6">
                  Louez le{' '}
                  <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
                    Véhicule Parfait
                  </span>
                </h1>
                <p className="text-xl text-blue-100/70 mb-10 max-w-3xl mx-auto leading-relaxed">
                  Large sélection de véhicules modernes avec ou sans chauffeur professionnel. Réservez en ligne en quelques clics.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="default"
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25 border-none rounded-xl px-8 py-3"
                      iconName="Calculator"
                      iconPosition="left"
                      onClick={() => setShowCalculator(true)}
                    >
                      Calculer le Prix
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-white/20 text-white hover:bg-white/5 backdrop-blur-sm rounded-xl px-8 py-3"
                      iconName="Phone"
                      iconPosition="left"
                      onClick={() => window.location.href = '/contact'}
                    >
                      Nous Contacter
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Filters Section */}
          <section className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-xl border-y border-slate-200 dark:border-white/10 sticky top-0 z-40">
            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 py-4">
              <div className="flex flex-col lg:flex-row items-center gap-4">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Icon name="Filter" size={20} className="text-blue-400" />
                  <span className="font-semibold">Filtrer:</span>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 w-full lg:w-auto">
                  <Select
                    options={vehicleTypeOptions}
                    value={selectedType}
                    onChange={setSelectedType}
                    placeholder="Type de véhicule"
                  />
                  <Select
                    options={transmissionOptions}
                    value={selectedTransmission}
                    onChange={setSelectedTransmission}
                    placeholder="Transmission"
                  />
                  <Select
                    options={priceRangeOptions}
                    value={priceRange}
                    onChange={setPriceRange}
                    placeholder="Gamme de prix"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RotateCcw"
                  onClick={() => {
                    setSelectedType('all');
                    setSelectedTransmission('all');
                    setPriceRange('all');
                  }}
                  className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Réinitialiser
                </Button>
              </div>
            </div>
          </section>

          {/* Vehicles Grid */}
          <section className="py-12 md:py-16 lg:py-20">
            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
              {loading ? (
                <div className="text-center py-20">
                  <div className="w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mx-auto" />
                  <p className="mt-4 text-slate-600 dark:text-slate-400">Chargement des véhicules...</p>
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
                  <p className="text-red-400">Erreur: {error}</p>
                </div>
              ) : filteredVehicles?.length === 0 ? (
                <motion.div
                  className="text-center py-20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-24 h-24 rounded-2xl bg-white/80 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 flex items-center justify-center mx-auto mb-6">
                    <Icon name="Car" size={40} className="text-slate-500" />
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 mb-8">Aucun véhicule disponible avec ces filtres</p>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="default"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={() => {
                        setSelectedType('all');
                        setSelectedTransmission('all');
                        setPriceRange('all');
                      }}
                      className="bg-blue-600 hover:bg-blue-500 text-white border-none rounded-xl px-8 py-3"
                    >
                      Réinitialiser les filtres
                    </Button>
                  </motion.div>
                </motion.div>
              ) : (
                <>
                  <motion.div
                    className="flex items-center justify-between mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                      Véhicules Disponibles
                    </h2>
                    <span className="text-slate-600 dark:text-slate-400">
                      {filteredVehicles?.length} véhicule{filteredVehicles?.length > 1 ? 's' : ''}
                    </span>
                  </motion.div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {filteredVehicles?.map((vehicle) => (
                      <VehicleCard
                        key={vehicle?.id}
                        vehicle={vehicle}
                        onRent={handleRentVehicle}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Features Section */}
          <section className="py-16 md:py-20 lg:py-28 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat',
              }}
            />
            <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />

            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
              <motion.div
                className="text-center mb-12 md:mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7 }}
              >
                <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-5 py-2.5 mb-6">
                  <Icon name="Star" size={18} className="text-amber-400" />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Nos Avantages</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-5">
                  Pourquoi Choisir{' '}
                  <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
                    ORAZEK?
                  </span>
                </h2>
                <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
                  Nous offrons une expérience de location exceptionnelle avec des services premium
                </p>
              </motion.div>

              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.12, delayChildren: 0.1 }
                  }
                }}
              >
                {[
                  {
                    icon: 'Shield',
                    title: 'Assurance Complète',
                    description: 'Tous nos véhicules sont entièrement assurés'
                  },
                  {
                    icon: 'Users',
                    title: 'Chauffeurs Professionnels',
                    description: 'Équipe expérimentée et certifiée'
                  },
                  {
                    icon: 'Clock',
                    title: 'Disponibilité 24/7',
                    description: 'Service client disponible à tout moment'
                  },
                  {
                    icon: 'DollarSign',
                    title: 'Prix Compétitifs',
                    description: 'Meilleurs tarifs du marché garantis'
                  }
                ]?.map((feature, index) => (
                  <motion.div
                    key={index}
                    variants={{
                      hidden: { opacity: 0, y: 40 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                    }}
                    className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-8 md:p-10 hover:border-blue-500/30 shadow-2xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-900/30">
                      <Icon name={feature?.icon} size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{feature?.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{feature?.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        </motion.main>

        <Footer />

        {/* Modals */}
        {showDriverModal && (
          <DriverSelectionModal
            isOpen={showDriverModal}
            onClose={() => setShowDriverModal(false)}
            onSelectDriver={handleDriverSelected}
            onSkip={handleSkipDriver}
            vehicle={selectedVehicle}
          />
        )}

        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            vehicle={selectedVehicle}
            driver={selectedDriver}
            onComplete={handleBookingComplete}
          />
        )}

        {showCalculator && (
          <PricingCalculator
            isOpen={showCalculator}
            onClose={() => setShowCalculator(false)}
          />
        )}
      </div>
    </>
  );
};

export default VehicleRental;
